interface ResourceItem {
  id: string;
  type: "image" | "script" | "style" | "component";
  url?: string;
  importFunc?: () => Promise<any>;
  priority: "critical" | "high" | "medium" | "low";
  preload?: boolean;
}

class ResourceManager {
  private resources: Map<string, ResourceItem> = new Map();
  private loadedResources: Set<string> = new Set();
  private loadingQueue: string[] = [];
  private isProcessing = false;

  constructor() {
    this.init();
  }

  private init() {
    // 监听页面可见性变化
    if ("hidden" in document) {
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
          this.pauseNonCriticalLoading();
        } else {
          this.resumeLoading();
        }
      });
    }

    // 监听网络状态变化
    if ("connection" in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        connection.addEventListener("change", () => {
          this.adjustLoadingStrategy();
        });
      }
    }
  }

  // 注册资源
  register(resource: ResourceItem) {
    this.resources.set(resource.id, resource);

    // 关键资源立即加载
    if (resource.priority === "critical") {
      this.loadResource(resource.id);
    }

    // 高优先级资源在空闲时间预加载
    if (resource.priority === "high" && resource.preload) {
      this.preloadResource(resource.id);
    }
  }

  // 加载资源
  private async loadResource(id: string) {
    const resource = this.resources.get(id);
    if (!resource || this.loadedResources.has(id)) {
      return;
    }

    try {
      if (resource.type === "image" && resource.url) {
        await this.loadImage(resource.url);
      } else if (resource.type === "script" && resource.url) {
        await this.loadScript(resource.url);
      } else if (resource.type === "style" && resource.url) {
        await this.loadStyle(resource.url);
      } else if (resource.type === "component" && resource.importFunc) {
        await resource.importFunc();
      }

      this.loadedResources.add(id);
      this.onResourceLoaded(id);
    } catch (error) {
      console.error(`Failed to load resource: ${id}`, error);
    }
  }

  // 加载图片
  private loadImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = url;
    });
  }

  // 加载脚本
  private loadScript(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  // 加载样式
  private loadStyle(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = url;
      link.onload = () => resolve();
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }

  // 预加载资源
  private preloadResource(id: string) {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => {
        this.loadResource(id);
      });
    } else {
      setTimeout(() => {
        this.loadResource(id);
      }, 1000);
    }
  }

  // 批量加载资源
  async loadResources(ids: string[]) {
    const promises = ids.map((id) => this.loadResource(id));
    await Promise.all(promises);
  }

  // 按优先级加载资源
  async loadByPriority(priority: "critical" | "high" | "medium" | "low") {
    const resources = Array.from(this.resources.values()).filter(
      (r) => r.priority === priority && !this.loadedResources.has(r.id)
    );

    for (const resource of resources) {
      await this.loadResource(resource.id);
    }
  }

  // 暂停非关键资源加载
  private pauseNonCriticalLoading() {
    // 实现暂停逻辑
  }

  // 恢复加载
  private resumeLoading() {
    // 实现恢复逻辑
  }

  // 调整加载策略
  private adjustLoadingStrategy() {
    const connection = (navigator as any).connection;
    if (connection) {
      if (
        connection.effectiveType === "slow-2g" ||
        connection.effectiveType === "2g"
      ) {
        // 慢速网络：只加载关键资源
        this.loadByPriority("critical");
      } else if (connection.effectiveType === "3g") {
        // 中等网络：加载关键和高优先级资源
        this.loadByPriority("critical");
        this.loadByPriority("high");
      }
    }
  }

  // 资源加载完成回调
  private onResourceLoaded(id: string) {
    // 可以在这里添加统计、日志等功能
    console.log(`Resource loaded: ${id}`);
  }

  // 获取资源状态
  getResourceStatus(id: string) {
    return {
      isLoaded: this.loadedResources.has(id),
      isRegistered: this.resources.has(id),
    };
  }

  // 清理资源
  clear() {
    this.resources.clear();
    this.loadedResources.clear();
    this.loadingQueue = [];
  }
}

// 创建单例实例
export const resourceManager = new ResourceManager();

// 导出便捷方法
export const registerResource = (resource: ResourceItem) => {
  resourceManager.register(resource);
};

export const loadResources = (ids: string[]) => {
  return resourceManager.loadResources(ids);
};

export const loadByPriority = (
  priority: "critical" | "high" | "medium" | "low"
) => {
  return resourceManager.loadByPriority(priority);
};
